import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import React, { useEffect, useRef, useState } from "react";
import { tokens } from "styles/Tokens";
import Typography from "../Typography";
import "./index.scss";

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

const normalizeDate = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const isSameDay = (a?: Date | null, b?: Date | null) =>
  !!a && !!b && normalizeDate(a).getTime() === normalizeDate(b).getTime();

const isBetween = (d: Date, start?: Date | null, end?: Date | null) =>
  !!start && !!end && normalizeDate(d) > normalizeDate(start) && normalizeDate(d) < normalizeDate(end);

const MONTHS = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat("en-US", { month: "long" }).format(new Date(2025, i, 1))
);

const YEARS = Array.from({ length: 50 }, (_, i) => 2023 + i);

const DateRangePickerCalendar: React.FC<any> = ({ dateRangeValue, parent, onChange }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [showMonthList, setShowMonthList] = useState(false);
  const [showYearList, setShowYearList] = useState(false);

  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dateRangeValue) return;

    setDateRange({
      startDate: dateRangeValue.startDate && normalizeDate(new Date(dateRangeValue.startDate)),
      endDate: dateRangeValue.endDate && normalizeDate(new Date(dateRangeValue.endDate))
    });
  }, [dateRangeValue]);

  useEffect(() => {
    if (dateRange.startDate) {
      setCalendarDate(new Date(dateRange.startDate.getFullYear(), dateRange.startDate.getMonth(), 1));
    }
  }, [dateRange.startDate]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;

      if (pickerRef.current && !pickerRef.current.contains(target)) {
        setShowCalendar(false);
        setShowMonthList(false);
        setShowYearList(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleDateSelect = (date: Date) => {
    setDateRange(({ startDate, endDate }) => {
      if (!startDate || endDate || date < startDate) {
        return { startDate: date, endDate: null };
      }

      const range = { startDate, endDate: date };
      setShowCalendar(false);
      onChange?.(range, parent);
      return range;
    });
  };

  const changeMonth = (offset: number) => setCalendarDate((d) => new Date(d.getFullYear(), d.getMonth() + offset, 1));

  const selectMonth = (month: number) => {
    setCalendarDate((d) => new Date(d.getFullYear(), month, 1));
    setShowMonthList(false);
  };

  const selectYear = (year: number) => {
    setCalendarDate((d) => new Date(year, d.getMonth(), 1));
    setShowYearList(false);
  };

  const renderCalendar = () => {
    if (!showCalendar) return null;

    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const today = normalizeDate(new Date());

    const days = [
      ...Array(firstDay).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))
    ];

    return (
      <div
        className="calendar-container"
        ref={calendarRef}
        onClick={() => {
          setShowMonthList(false);
          setShowYearList(false);
        }}
      >
        <div className="calendar-header">
          <div className="month-label">
            <div className="dropdown-wrapper">
              <span
                className="clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMonthList((v) => !v);
                  setShowYearList(false);
                }}
              >
                <Typography showBold color={tokens.Text.General.PrimaryBody}>
                  {MONTHS[month]}
                </Typography>
              </span>

              {showMonthList && (
                <div className="dropdown" ref={monthDropdownRef}>
                  {MONTHS.map((m, i) => (
                    <div key={m} className="dropdown-item" onClick={() => selectMonth(i)}>
                      {m}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="dropdown-wrapper">
              <span
                className="clickable"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowYearList((v) => !v);
                  setShowMonthList(false);
                }}
              >
                <Typography color={tokens.Text.General.PrimaryBody}>{year}</Typography>
              </span>

              {showYearList && (
                <div className="dropdown" ref={yearDropdownRef}>
                  {YEARS.map((y) => (
                    <div key={y} className="dropdown-item" onClick={() => selectYear(y)}>
                      {y}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <span className="nav-controls">
            <span className="nav-arrow" onClick={() => changeMonth(-1)}>
              ‹
            </span>
            <span className="dot-separator" />
            <span className="nav-arrow" onClick={() => changeMonth(1)}>
              ›
            </span>
          </span>
        </div>

        <div className="calendar-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="calendar-weekday">
              {d}
            </div>
          ))}

          {days.map((date, i) =>
            date ? (
              <div
                key={i}
                className={`calendar-day
                  ${isSameDay(date, dateRange.startDate) ? "range-start" : ""}
                  ${isSameDay(date, dateRange.endDate) ? "range-end" : ""}
                  ${isBetween(date, dateRange.startDate, dateRange.endDate) ? "in-range" : ""}
                  ${normalizeDate(date) <= today ? "selectable" : "disabled"}
                `}
                onClick={() => normalizeDate(date) <= today && handleDateSelect(normalizeDate(date))}
              >
                {date.getDate()}
              </div>
            ) : (
              <div key={i} className="calendar-day empty" />
            )
          )}
        </div>
      </div>
    );
  };

  const getSelectedText = () =>
    dateRange.startDate && dateRange.endDate
      ? `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`
      : dateRange.startDate
      ? dateRange.startDate.toLocaleDateString()
      : "Date Range";

  return (
    <div ref={pickerRef} className="date-range-picker-wrapper">
      <div className="custom-date-input" onClick={() => setShowCalendar((v) => !v)}>
        <span className="date-range-input">{getSelectedText()}</span>
        {showCalendar ? <ArrowUp /> : <ArrowDown />}
      </div>
      {renderCalendar()}
    </div>
  );
};

export default DateRangePickerCalendar;
